'use client';

import { useDebounce } from '@uidotdev/usehooks';
import { Button } from 'components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Input } from 'components/ui/input';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSubButton,
} from 'components/ui/sidebar';
import { Role } from 'consts/auth';
import { UPGRADE_ACCESS_MODAL_ID } from 'consts/modals';
import { requiresFullAccess } from 'consts/route-access';
import { useModals } from 'contexts/modals';
import { useHasRole } from 'hooks/use-auth';
import map from 'lodash/map';
import { Calendar, ChevronRight, Lock, MessageCircle, Search, SlidersHorizontal, SquarePen } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';

import { ChatSidebarItem } from '../pages/Chat/components/ChatSidebarItem';
import { useChatGroups } from '../pages/Chat/hooks/use-chat-groups';
import { useChats } from '../pages/Chat/hooks/use-chats';
import { SidebarFloatingMenu } from './sidebar-floating-menu';
import { SIDEBAR_SECTION_LABEL_CLASS } from './sidebar-section-label';

// Locking is derived per-URL from consts/route-access, so Chat stays open and
// /scheduled-tasks + /customize lock without restating their roles here.
const AGENTS_FLOATING_ITEMS = [
  { title: 'Chat', url: '/chat' },
  { title: 'Scheduled', url: '/scheduled-tasks' },
  { title: 'Customize', url: '/customize' },
];

// Expanded-sidebar rows under Agents. Whether each locks is derived from
// consts/route-access (both are FullAccess today; Chat itself stays open).
const AGENT_LINKS = [
  { title: 'Scheduled Tasks', url: '/scheduled-tasks', icon: Calendar },
  { title: 'Customize', url: '/customize', icon: SlidersHorizontal },
];

// Bare <SidebarMenuItem> for the AGENTS entry in icon-collapsed mode. Exported
// so AppSidebar can inline it inside the apps SidebarMenu and avoid the
// 20px gap caused by two separate SidebarGroups.
export function NavAgentsCollapsedItem () {
  const hasFullAccess = useHasRole(Role.FullAccess);
  const { openModal } = useModals();
  const items = map(AGENTS_FLOATING_ITEMS, (item) => ({
    ...item,
    locked: !hasFullAccess && requiresFullAccess(item.url),
  }));

  return (
    <SidebarMenuItem>
      <SidebarFloatingMenu
        title="Agents"
        items={items}
        onLockedClick={() => openModal(UPGRADE_ACCESS_MODAL_ID)}
        trigger={
          <SidebarMenuButton asChild>
            <a href="/chat">
              <MessageCircle />
              <span>Agents</span>
            </a>
          </SidebarMenuButton>
        }
      />
    </SidebarMenuItem>
  );
}

export function NavAgents () {
  const history = useHistory();
  const location = useLocation();
  const hasFullAccess = useHasRole(Role.FullAccess);
  const { openModal } = useModals();
  // matchPath (react-router-dom v5) lets NavAgents read /chat/:id without
  // being inside the matched <Route>. Returns null when pathname doesn't match.
  const chatMatch = matchPath<{ id?: string }>(location.pathname, {
    path: '/chat/:id',
    exact: true,
  });
  const urlChatId = chatMatch?.params.id;

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 200);

  const { chats, fetchNextPage, hasNextPage, isFetchingNextPage } = useChats(debouncedSearch || undefined);
  const groups = useChatGroups(chats);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const handleNewChat = useCallback(() => {
    history.push('/chat');
  }, [history]);

  // Auto-load next page when the sentinel is visible inside whichever ancestor
  // is scrolling (now the SidebarContent itself). rootMargin lets us prefetch
  // slightly before the user hits the bottom.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage || isFetchingNextPage) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) fetchNextPage();
      },
      { rootMargin: '100px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, chats]);

  return (
    <Collapsible defaultOpen className="group/agents">
      <SidebarGroup>
        <CollapsibleTrigger asChild>
          <SidebarGroupLabel className={`cursor-pointer flex items-center justify-between shrink-0 pr-0 ${SIDEBAR_SECTION_LABEL_CLASS}`}>
            Agents
            <span className="p-1">
              <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]/agents:rotate-90" />
            </span>
          </SidebarGroupLabel>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarGroupContent className="flex flex-col gap-1 px-2 pt-0.5">
            {map(AGENT_LINKS, (link) => {
              const locked = !hasFullAccess && requiresFullAccess(link.url);
              return (
                <SidebarMenuSubButton
                  key={link.url}
                  asChild
                  size="sm"
                  isActive={location.pathname === link.url}
                >
                  {locked ? (
                    // Keep the label and pitch the upgrade — navigating would
                    // only bounce them back to /chat via PrivateRoute.
                    <button type="button" onClick={() => openModal(UPGRADE_ACCESS_MODAL_ID)}>
                      <link.icon className="opacity-70" />
                      {/* The sub-button's [&>span:last-child]:truncate stops
                          matching once the lock trails the label. */}
                      <span className="truncate">{link.title}</span>
                      <Lock className="ml-auto !size-3.5 opacity-60" />
                    </button>
                  ) : (
                    <a href={link.url}>
                      <link.icon className="opacity-70" />
                      <span>{link.title}</span>
                    </a>
                  )}
                </SidebarMenuSubButton>
              );
            })}
            <Button size="xs" onClick={handleNewChat} className="w-full">
              <SquarePen />
              New Chat
            </Button>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-6 h-6 text-xs md:text-xs shadow-none"
              />
            </div>
            {map(groups, (group) => (
              <div key={group.label} className="mb-2">
                <div className="px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                  {group.label}
                </div>
                <ul className="flex flex-col gap-0.5">
                  {map(group.chats, (chat) => (
                    <ChatSidebarItem
                      key={chat.id}
                      chat={chat}
                      isActive={chat.id === urlChatId}
                    />
                  ))}
                </ul>
              </div>
            ))}
            <div ref={sentinelRef} aria-hidden="true" />
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}
